import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface OrganizationAssignedDevice {
  device_id: string;
  device_name: string | null;
  device_description: string | null;
  device_status: string | null;
  assigned_at: string;
  total_credits: number;
  coverage_years: number | null;
  plan_type: string | null;
}

export const useOrganizationAssignedDevices = (organizationId: string | null) => {
  return useQuery({
    queryKey: ['organization-assigned-devices', organizationId],
    queryFn: async (): Promise<OrganizationAssignedDevice[]> => {
      if (!organizationId) return [];

      // Step 1: Get all users in the organization
      const { data: orgUsers, error: usersError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('organization_id', organizationId);

      if (usersError) {
        console.error('Error fetching organization users:', usersError);
        throw usersError;
      }

      if (!orgUsers || orgUsers.length === 0) {
        return [];
      }

      const userIds = orgUsers.map(u => u.user_id);

      // Step 2: Get device assignments for all organization users
      const { data: assignments, error: assignmentsError } = await supabase
        .from('device_assignments')
        .select('device_id, assigned_at, user_id')
        .in('user_id', userIds);

      if (assignmentsError) {
        console.error('Error fetching device assignments:', assignmentsError);
        throw assignmentsError;
      }

      if (!assignments || assignments.length === 0) {
        return [];
      }

      const deviceIds = [...new Set(assignments.map(a => a.device_id))];

      // Step 3: Get device details
      const { data: devices, error: devicesError } = await supabase
        .from('device_details')
        .select('device_id, device_name, device_description, device_status')
        .in('device_id', deviceIds);

      if (devicesError) {
        console.error('Error fetching device details:', devicesError);
        throw devicesError;
      }

      // Step 4: Get credit packages for these devices (for starting credits)
      const { data: creditPackages, error: creditsError } = await supabase
        .from('device_credit_packages')
        .select('device_id, total_credits, coverage_year, package_name')
        .in('device_id', deviceIds);

      if (creditsError) {
        console.error('Error fetching credit packages:', creditsError);
        // Don't throw - credits are optional
      }

      // Step 5: Get order/deal info for contract details
      const { data: shipments, error: shipmentsError } = await supabase
        .from('order_shipments')
        .select(`
          allocated_device_ids,
          allocated_credits,
          order_id,
          orders!inner (
            sale_id,
            sales!inner (
              deal_id,
              crm_deals (
                deal_data
              )
            )
          )
        `)
        .not('allocated_device_ids', 'is', null);

      // Build a map of device to contract info
      const deviceContractMap = new Map<string, { credits: number; years: number | null; plan: string | null }>();
      
      if (shipments) {
        for (const shipment of shipments) {
          const allocatedDevices = shipment.allocated_device_ids || [];
          for (const devId of allocatedDevices) {
            if (deviceIds.includes(devId)) {
              const existing = deviceContractMap.get(devId) || { credits: 0, years: null, plan: null };
              existing.credits += shipment.allocated_credits || 0;
              
              // Extract deal data for contract info
              const dealData = (shipment.orders as any)?.sales?.crm_deals?.deal_data;
              if (dealData) {
                if (dealData.coverage_years) existing.years = dealData.coverage_years;
                if (dealData.plan) existing.plan = dealData.plan;
              }
              
              deviceContractMap.set(devId, existing);
            }
          }
        }
      }

      // Step 6: Aggregate credit info from credit packages if not from shipments
      const deviceCreditMap = new Map<string, number>();
      if (creditPackages) {
        for (const pkg of creditPackages) {
          const existing = deviceCreditMap.get(pkg.device_id) || 0;
          deviceCreditMap.set(pkg.device_id, existing + pkg.total_credits);
        }
      }

      // Step 7: Build the result combining all data
      const deviceMap = new Map<string, OrganizationAssignedDevice>();
      
      for (const assignment of assignments) {
        const device = devices?.find(d => d.device_id === assignment.device_id);
        if (!device) continue;

        const existing = deviceMap.get(assignment.device_id);
        const contractInfo = deviceContractMap.get(assignment.device_id);
        const packageCredits = deviceCreditMap.get(assignment.device_id) || 0;
        
        // Use earliest assignment date
        const currentAssignedAt = existing?.assigned_at ? new Date(existing.assigned_at) : null;
        const newAssignedAt = new Date(assignment.assigned_at);
        const earliestDate = !currentAssignedAt || newAssignedAt < currentAssignedAt 
          ? assignment.assigned_at 
          : existing?.assigned_at || assignment.assigned_at;

        deviceMap.set(assignment.device_id, {
          device_id: device.device_id,
          device_name: device.device_name,
          device_description: device.device_description,
          device_status: device.device_status,
          assigned_at: earliestDate,
          total_credits: packageCredits || contractInfo?.credits || 0,
          coverage_years: contractInfo?.years || null,
          plan_type: contractInfo?.plan || null,
        });
      }

      // Sort by assigned date descending
      return Array.from(deviceMap.values()).sort((a, b) => 
        new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime()
      );
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
